import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { Input as InputPrimitive } from '#/modules/commonComponents/shadcn/ui/input';

/**
 * 合併 callback／object ref（RHF register.ref + 內部 DOM ref）。
 * @param {HTMLElement | null} node
 * @param {import('react').Ref<HTMLElement> | undefined} ref
 * @param {import('react').MutableRefObject<HTMLElement | null>} localRef
 */
function assignMergedRef(node, ref, localRef) {
  localRef.current = node;
  if (typeof ref === 'function') {
    ref(node);
    return;
  }
  if (ref && typeof ref === 'object') {
    ref.current = node;
  }
}

/**
 * shadcn Input 薄包裝。
 * `type="number"` 預設擋滾輪改值（native wheel + passive:false；勿依賴 React onWheel preventDefault）。
 * `allowWheelChange` 時不掛阻擋 listener。
 */
const Input = ({
  type,
  allowWheelChange = false,
  onWheel,
  ref,
  ...props
}) => {
  const localRef = useRef(null);
  const shouldBlockWheel = type === 'number' && !allowWheelChange;

  useEffect(() => {
    const node = localRef.current;
    if (!node || !shouldBlockWheel) {
      return undefined;
    }

    function handleWheel(event) {
      event.preventDefault();
      onWheel?.(event);
    }

    node.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      node.removeEventListener('wheel', handleWheel);
    };
  }, [shouldBlockWheel, onWheel]);

  return (
    <InputPrimitive
      {...props}
      type={type}
      ref={(node) => {
        assignMergedRef(node, ref, localRef);
      }}
      onWheel={shouldBlockWheel ? undefined : onWheel}
    />
  );
};

Input.propTypes = {
  type: PropTypes.string,
  allowWheelChange: PropTypes.bool,
  onWheel: PropTypes.func,
  ref: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

export { Input };
